import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { AppRoutes } from 'constants/routes';

import AssetRegistryVotingLoading from 'web/pages/asset-registry/pages/AssetRegistryVotingPage/AssetRegistryVotingLoading';
import MarketPageLoading from 'web/pages/market/pages/MarketPage/MarketPageLoading';
import RewardsPageLoading from 'web/pages/rewards/RewardsPageLoading';
import SwapPageLoading from 'web/pages/swap/pages/SwapPageLoading';
import {
    ExploreBlock,
    PairSearch,
    ResultsPanel,
} from 'web/pages/vote/components/MainPage/MainPage.layout';
import VoteTableLoading from 'web/pages/vote/components/MainPage/Table/VoteTableLoading';

import DelegateBlockSmall from 'components/DelegateBlockSmall';
import VotingPageHero from 'components/VotingPageHero/VotingPageHero';

import AllPoolsLoading from 'pages/amm/components/AllPools/AllPoolsLoading';
import CreatePoolLoading from 'pages/amm/components/PoolPageLoading/CreatePoolLoading';
import PoolPageLoading from 'pages/amm/components/PoolPageLoading/PoolPageLoading';
import DelegatePageLoading from 'pages/delegate/pages/DelegatePageLoading';
import CreateProposal from 'pages/governance/components/GovernanceMainPage/CreateProposal/CreateProposal';
import ProposalListLoading from 'pages/governance/components/GovernanceMainPage/ProposalPreview/ProposalListLoading';
import ProposalLoading from 'pages/governance/components/GovernanceVoteProposalPage/Proposal/ProposalLoading';
import {
    ProposalList,
    ProposalsBlock,
    ProposalsBlockWrapper,
    ProposalsTitle,
    SidebarColumn,
    TitleBlock,
} from 'pages/governance/pages/GovernanceMainPage.styled';

import { Controls, ControlShape, Page, SearchFields } from './RouteLoading.styled';

// Keep the page shell visible before its data and lazy route module are ready.
const RouteLoading = () => {
    const { pathname } = useLocation();
    const path = pathname.replace(/\/$/, '');

    if (path === AppRoutes.section.delegate.base.replace(/\/$/, '')) return <DelegatePageLoading />;
    if (path === AppRoutes.page.rewards.replace(/\/$/, '')) return <RewardsPageLoading />;

    if (path === AppRoutes.page.vote.replace(/\/$/, '')) {
        return (
            <Page $plain>
                <VotingPageHero kind="markets" />
                <ExploreBlock $hasChosenPairs={false}>
                    <PairSearch>
                        <SearchFields role="status" aria-label="Loading market search">
                            <ControlShape $width="100%" />
                        </SearchFields>
                    </PairSearch>
                    <DelegateBlockSmall />
                    <ResultsPanel>
                        <Controls role="status" aria-label="Loading market filters">
                            <ControlShape $width="70%" />
                        </Controls>
                        <VoteTableLoading />
                    </ResultsPanel>
                </ExploreBlock>
            </Page>
        );
    }

    if (path === AppRoutes.section.governance.base.replace(/\/$/, '')) {
        return (
            <Page $plain>
                <VotingPageHero kind="governance" />
                <ProposalsBlockWrapper>
                    <ProposalsBlock>
                        <ProposalList>
                            <TitleBlock>
                                <ProposalsTitle>Proposals</ProposalsTitle>
                                <ControlShape
                                    $width="100%"
                                    role="status"
                                    aria-label="Loading proposal filters"
                                />
                            </TitleBlock>
                            <ProposalListLoading />
                        </ProposalList>
                        <SidebarColumn>
                            <CreateProposal />
                        </SidebarColumn>
                    </ProposalsBlock>
                </ProposalsBlockWrapper>
            </Page>
        );
    }

    if (path === AppRoutes.section.amm.base.replace(/\/$/, '')) return <AllPoolsLoading />;
    if (path === AppRoutes.section.amm.link.create) return <CreatePoolLoading />;
    if (pathname.startsWith(AppRoutes.section.amm.base)) return <PoolPageLoading />;
    if (pathname.startsWith(AppRoutes.section.swap.base)) return <SwapPageLoading />;
    if (pathname.startsWith(AppRoutes.section.market.base)) return <MarketPageLoading />;
    if (pathname.startsWith(`${AppRoutes.section.governance.base}proposal/`))
        return <ProposalLoading />;
    if (/^\/asset-registry\/\d+\/?$/.test(pathname)) return <AssetRegistryVotingLoading />;

    return <Page />;
};

export default RouteLoading;
